import type { AutoAnnotateData } from '@/modules/Editor/AnnotationData'
import type { Click, InferenceResult } from '@/modules/Editor/backend'
import type { Rectangle } from '@/modules/Editor/rectangle'
import type { ClickerTool } from '@/modules/Editor/plugins/click/ClickerTool'

import { payloadRelativeToCentroid } from './payloadRelativeToCentroid'
import { retrieveAutoAnnotateData } from './retrieveAutoAnnotateData'
import { calcCentroidByType } from '@/modules/Editor/utils/calcCentroidByType'
import type { Editor } from '@/modules/Editor/editor'
// eslint-disable-next-line boundaries/element-types
import type { ClassMapping } from '@/modules/Workview/classMappingTypes'
import type { AutoAnnotateModel } from '@/modules/Editor/types'
import { remapInferenceResult } from './remapInferenceResult'
import { createAnnotationFromInferenceData } from '@/modules/Editor/models/annotation/annotationFactories'
import type { PointMapping } from '@/modules/Editor/plugins/click/types'
import { getMainAnnotationType } from '@/core/annotationTypes'
import { isImageAnnotation } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { isVideoAnnotation } from '@/modules/Editor/models/annotation/annotationKindValidator'
import { shallowCloneAnnotation } from '@/modules/Editor/models/annotation/cloneAnnotation'

export class ExternalInferredClassMappingError extends Error {
  constructor(
    message: string,
    public readonly label: string,
  ) {
    super(message)
    this.name = 'ExternalInferredClassMappingError'
  }
}

export const processExternalInferredAnnotation = async (
  clickTool: ClickerTool,
  editor: Editor,
  preselectedModelClassMapping: ClassMapping,
  mapping: PointMapping,
  model: AutoAnnotateModel,
  clicks: Click[],
  bbox: Rectangle,
  results: InferenceResult[],
): Promise<void> => {
  const { context } = clickTool
  if (!context) {
    throw new Error('Clicker tool has no context. It might not have been properly initialized')
  }

  for (const result of results) {
    const inferenceData = remapInferenceResult(result, mapping)
    const annotation = createAnnotationFromInferenceData(inferenceData, model.classes)
    if (!annotation) {
      return
    }

    const classMapping = preselectedModelClassMapping.find(
      (m) => m.modelClassLabel === annotation.label,
    )
    if (!classMapping || !classMapping.annotationClassId) {
      throw new ExternalInferredClassMappingError(
        `No class mapping found for model class label: ${annotation.label}`,
        annotation.label,
      )
    }

    const annotationClass = editor.getClassById(classMapping.annotationClassId)
    if (!annotationClass) {
      return
    }

    annotation.annotationClass = annotationClass

    const mainAnnotationType = getMainAnnotationType(annotationClass.annotation_types)

    if (!mainAnnotationType) {
      return
    }

    const centroid = calcCentroidByType(mainAnnotationType, result)

    if (!centroid) {
      return
    }

    const centroidRelativePayload = payloadRelativeToCentroid({ clicks, bbox }, centroid)

    const autoAnnotateData: AutoAnnotateData = {
      clicks: centroidRelativePayload.clicks,
      bbox: centroidRelativePayload.bbox,
      model: model.id,
    }

    clickTool.actionGroup = clickTool.actionGroup || context.editor.actionManager.createGroup()

    const { currentAnnotation } = clickTool

    if (currentAnnotation) {
      const { activeView } = context.editor

      const annotationToBeEdited = activeView.annotationManager.getAnnotation(currentAnnotation.id)
      if (!annotationToBeEdited) {
        throw new Error('processExternalInferredAnnotation, no annotation found')
      }

      const previousClicks = [...clickTool.currentClicks]
      const previousAnnotation = shallowCloneAnnotation(annotationToBeEdited, {
        data: annotationToBeEdited.data,
      })
      const previousAutoAnnotateData = retrieveAutoAnnotateData(
        annotationToBeEdited.id,
        context.editor.activeView,
      )

      clickTool.actionGroup.do({
        do(): Promise<boolean> {
          const isVideoAnn = isVideoAnnotation(annotationToBeEdited)

          if (isVideoAnn) {
            const frameIndex = activeView.currentFrameIndex
            if (frameIndex === undefined || frameIndex === -1) {
              throw new Error('Clicker: Trying to update video annotation without video loaded')
            }

            const frame = annotationToBeEdited.data.frames[frameIndex]

            annotationToBeEdited.data.frames[frameIndex] = {
              ...frame,
              ...annotation.data,
              auto_annotate: autoAnnotateData,
            }
          } else if (isImageAnnotation(annotationToBeEdited)) {
            const autoAnnotateSub =
              context.editor.activeView.annotationManager.initializeSubAnnotation(
                'auto_annotate',
                annotationToBeEdited,
                autoAnnotateData,
              )

            const index = annotationToBeEdited.subAnnotations.findIndex(
              (elem) => elem.type === 'auto_annotate',
            )

            if (index !== -1) {
              annotationToBeEdited.subAnnotations.splice(index, 1)
            }

            annotationToBeEdited.subAnnotations.push(autoAnnotateSub)
            annotationToBeEdited.data = {
              ...annotation.data,
              auto_annotate: autoAnnotateData,
            }
          } else {
            throw new Error('processExternalInferredAnnotation, invalid annotation type')
          }

          // Force the new annotation to not be highlighted or selected
          context.editor.activeView.annotationManager.selectAnnotations([])

          activeView?.annotationManager.updateAnnotation(
            annotationToBeEdited,
            isVideoAnn
              ? {
                  updatedFramesIndices: [activeView.currentFrameIndex],
                }
              : undefined,
          )

          context.editor.activeView.annotationManager.selectAnnotations([annotationToBeEdited.id])
          clickTool.currentAnnotation = annotationToBeEdited
          clickTool.currentClicks = clicks
          clickTool.currentVisibleClicks = clicks
          context.editor.callCommand('clicker_tool.resume')
          clickTool.initialAnnotationData = undefined

          return Promise.resolve(true)
        },
        undo(): Promise<boolean> {
          if (isVideoAnnotation(previousAnnotation)) {
            const frameIndex = activeView.currentFrameIndex
            if (frameIndex === undefined || frameIndex === -1) {
              throw new Error('Clicker: Trying to update video annotation without video loaded')
            }

            const frame = previousAnnotation.data.frames[frameIndex]

            previousAnnotation.data.frames[frameIndex] = {
              ...frame,
              ...previousAnnotation.data,
              auto_annotate: previousAutoAnnotateData,
            }
          } else if (isImageAnnotation(previousAnnotation)) {
            const autoAnnotateSub =
              context.editor.activeView.annotationManager.initializeSubAnnotation(
                'auto_annotate',
                previousAnnotation,
                previousAutoAnnotateData,
              )

            const index = previousAnnotation.subAnnotations.findIndex(
              (elem) => elem.type === 'auto_annotate',
            )

            if (index !== -1) {
              previousAnnotation.subAnnotations.splice(index, 1)
            }

            previousAnnotation.subAnnotations.push(autoAnnotateSub)
            previousAnnotation.data = {
              ...previousAnnotation.data,
              auto_annotate: previousAutoAnnotateData,
            }
          }

          // Force the new annotation to not be highlighted or selected
          context.editor.activeView.annotationManager.selectAnnotations([])

          activeView?.annotationManager.updateAnnotation(
            previousAnnotation,
            isVideoAnnotation(previousAnnotation)
              ? {
                  updatedFramesIndices: [activeView.currentFrameIndex],
                }
              : undefined,
          )

          context.editor.activeView.annotationManager.selectAnnotations([previousAnnotation.id])
          clickTool.currentAnnotation = previousAnnotation
          clickTool.currentClicks = previousClicks
          clickTool.currentVisibleClicks = previousClicks
          context.editor.callCommand('clicker_tool.resume')
          clickTool.initialAnnotationData = undefined

          return Promise.resolve(true)
        },
      })
    } else {
      const newAnnotation =
        await context.editor.activeView.annotationManager.prepareAnnotationForCreation(annotation, {
          clicker: autoAnnotateData,
        })

      if (!newAnnotation) {
        return
      }

      clickTool.actionGroup.do({
        do(): Promise<boolean> {
          context.editor.activeView.annotationManager.createAnnotation(newAnnotation)
          context.editor.activeView.annotationManager.selectAnnotations([newAnnotation.id])
          clickTool.currentAnnotation = newAnnotation
          context.editor.callCommand('clicker_tool.resume')
          clickTool.initialAnnotationData = undefined

          return Promise.resolve(true)
        },
        undo(): Promise<boolean> {
          context.editor.activeView.annotationManager.deleteAnnotation(newAnnotation.id)
          clickTool.reset(context)

          return Promise.resolve(true)
        },
      })
    }
  }
}
